import { FlexColumn, FlexRow, mediaQuery, simpleHash, useIsMobileAndTablet } from '@fuuze/shared';
import { useMeasure } from '@uidotdev/usehooks';
import { AnimatePresence, motion } from 'motion/react';
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';
import { type PodcastPlaying, usePlayerStore } from '../../store/player.store';
import commonStyles from '../../styles/common.module.css';
import styles from './player-collapsed.module.css';
import { PlayerControls } from './player-controls';
import { PlayerPlayButton } from './player-play-button';
import { PlayerSlider } from './player-slider';
import { PlayerSpotlightPreviewBanner } from './player-spotlight-preview-banner';
import { PlayerTitleMarquee } from './player-title-marquee';

type PlayerCollapsedProps = {
  wrapperStyles?: CSSProperties;
  podcastPlaying: PodcastPlaying;
};

export function PlayerCollapsed({ wrapperStyles, podcastPlaying }: PlayerCollapsedProps) {
  const isMobileAndTablet = useIsMobileAndTablet();
  const [ref, { height }] = useMeasure<HTMLDivElement>();
  const titleRef = useRef<HTMLDivElement>(null);
  const [setPlayerCollapsedHeight, spotlight] = usePlayerStore(
    useShallow(state => [state.setPlayerCollapsedHeight, state.spotlight]),
  );
  const navigate = useNavigate();

  const [playTitleMarquee, setPlayTitleMarquee] = useState(false);

  const title = spotlight?.preview?.title || podcastPlaying?.item.title;
  const playerTitleMarqueeKey = useMemo(() => simpleHash(title || ''), [title]);

  // Effects

  useEffect(() => {
    if (!titleRef.current) return;

    const interval = setInterval(() => {
      if (!titleRef.current) return;
      setPlayTitleMarquee((titleRef.current.parentNode as HTMLElement).clientWidth < titleRef.current.clientWidth);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setPlayerCollapsedHeight(height || 0);
  }, [height]);

  if (!podcastPlaying) return null;

  // Callbacks

  const goToPodcastEpisodeDetailPage = () => {
    navigate(`/podcast/${podcastPlaying.channel.slug}/episode/${podcastPlaying.item.slug}`);
  };

  const onChannelTitleClick = (event: MouseEvent) => {
    navigate(`/podcast/${podcastPlaying.channel.slug}`);
  };

  if (!isMobileAndTablet) {
    return (
      <>
        <PlayerSpotlightPreviewBanner spotlight={spotlight} />
        <Wrapper id='player' ref={ref} style={wrapperStyles} $highlight={!!spotlight}>
          <div
            className={styles.background}
            style={{ backgroundImage: `url(${podcastPlaying.item.image600 || podcastPlaying.channel.image600})` }}
          />
          <LeftSideWithCover podcastPlaying={podcastPlaying} />

          <TitleWrapper onClick={goToPodcastEpisodeDetailPage} style={{ maxWidth: 300 }}>
            {/* Hidden title to know when running the marquee */}
            <Title
              tabIndex={-1}
              aria-hidden
              ref={titleRef}
              style={{ visibility: 'hidden', height: 0, width: 'fit-content', position: 'absolute' }}
            >
              {podcastPlaying.item.title}
            </Title>

            <AnimatePresence mode='wait'>
              <motion.div
                key={playerTitleMarqueeKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={commonStyles.FlexColumn}
                style={{ gap: 0 }}
              >
                <PlayerTitleMarquee play={playTitleMarquee} delay={2} speed={30}>
                  <Title>{podcastPlaying.item.title}</Title>
                </PlayerTitleMarquee>
                <button type='button' onClick={onChannelTitleClick} className={styles.podcastName}>
                  {podcastPlaying.channel.title}
                </button>
              </motion.div>
            </AnimatePresence>
          </TitleWrapper>

          <FlexColumn style={{ flexGrow: 1, margin: '0 auto', maxWidth: 992, gap: 0, justifyContent: 'space-around' }}>
            <PlayerControls podcast={podcastPlaying.channel} episode={podcastPlaying.item} playIconSize={42} />
            <PlayerSlider />
          </FlexColumn>
        </Wrapper>
      </>
    );
  }

  return (
    <section style={{ position: 'relative' }}>
      <PlayerSpotlightPreviewBanner spotlight={spotlight} />
      <Wrapper id='player' ref={ref} style={wrapperStyles} $highlight={!!spotlight}>
        <div
          className={styles.background}
          style={{ backgroundImage: `url(${podcastPlaying.item.image600 || podcastPlaying.channel.image600})` }}
        />
        <LeftSideWithCover podcastPlaying={podcastPlaying} />

        <TitleSliderWrapper>
          {/* Hidden title to know when running the marquee */}
          <Title
            tabIndex={-1}
            aria-hidden
            ref={titleRef}
            style={{ visibility: 'hidden', height: 0, width: 'fit-content', position: 'absolute' }}
          >
            {podcastPlaying.item.title}
          </Title>

          {/* Title */}
          <AnimatePresence mode='wait'>
            <TitleWrapper
              onClick={onChannelTitleClick}
              key={playerTitleMarqueeKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <PlayerTitleMarquee play={playTitleMarquee} delay={2} speed={30}>
                <Title>{podcastPlaying.item.title}</Title>
              </PlayerTitleMarquee>
            </TitleWrapper>
          </AnimatePresence>

          <PlayerSlider />
        </TitleSliderWrapper>

        <PlayerPlayButton channel={podcastPlaying.channel} item={podcastPlaying.item} />
      </Wrapper>
    </section>
  );
}

function LeftSideWithCover({ podcastPlaying }: { podcastPlaying: PodcastPlaying }) {
  const navigate = useNavigate();

  const goToPodcastDetailPage = () => {
    navigate(`/podcast/${podcastPlaying?.channel.slug}/episode/${podcastPlaying?.item.slug}`);
  };

  return (
    <DefaultCoverWrapper
      key='collapsed-podcast-img'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={goToPodcastDetailPage}
    >
      <img src={podcastPlaying.item.image600} />
    </DefaultCoverWrapper>
  );
}

// --- STYLES ---

const Wrapper = styled(FlexRow)<{ $highlight?: boolean }>`
  height: 72px;
  border-radius: 8px;
  outline: 1px solid rgb(36, 36, 36);
  width: 100%;
  /* background: linear-gradient(
    172deg,
    hsl(40.71, 70%, 7.84%) 0%,
    hsl(40.71, 70%, 7.84%) 8%,
    hsl(41.54, 68.42%, 7.45%) 15.3%,
    hsl(40.8, 71.43%, 6.86%) 21.9%,
    hsl(41.74, 69.7%, 6.47%) 28.1%,
    hsl(42.86, 72.41%, 5.69%) 33.9%,
    hsl(40, 69.23%, 5.1%) 39.5%,
    hsl(41.25, 72.73%, 4.31%) 45%,
    hsl(40, 66.67%, 3.53%) 50.5%,
    hsl(42, 71.43%, 2.75%) 56.2%,
    hsl(34.29, 63.64%, 2.16%) 62.1%,
    hsl(36, 71.43%, 1.37%) 68.4%,
    hsl(40, 60%, 0.98%) 75.2%,
    hsl(30, 100%, 0.39%) 82.7%,
    hsl(0, 0%, 0%) 90.9%,
    hsl(0, 0%, 0%) 100%
  ); */
  position: relative;
  padding: 4px 8px;
  z-index: 2;
  gap: 0;
  overflow: hidden;

  /* &::after {
    content: '';
    border-radius: 8px;
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
  width: calc(100% - 2px); // 2 px to take account of the border when highlighted by the ::after due to a spotlight, to prevent a scroll on x axis
    height: 100%;
    box-shadow: 0px 0px 12px -3px #E8B59D;
    opacity: 0;
    transition: opacity 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
    border: 1px solid #8a604b;
  }

  ${props =>
    props.$highlight &&
    css`
      &::after {
        opacity: 1;
      }
  `} */

  @media ${mediaQuery.mobile} {
    padding: 8px;
    gap: 16px;
  }

  @media ${mediaQuery.tablet} {
    height: 88px;
  }

  @media ${mediaQuery.desktop} {
    gap: 8px;
  }
`;

const DefaultCoverWrapper = styled(motion.div)`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  width: 88px;
  gap: 8px;

  @media ${mediaQuery.mobile} {
    width: 104px;
  }

  @media ${mediaQuery.tablet} {
    width: 128px;
  }

  img {
    width: 48px;
    height: 48px;
    border-radius: 8px;

    @media ${mediaQuery.mobile} {
      width: 64px;
      height: 64px;
    }

    @media ${mediaQuery.xl} {
      width: 72px;
      height: 72px;
    }
  }
`;

const TitleSliderWrapper = styled(FlexColumn)`
  gap: 0;
  flex: 1;
  margin-bottom: 8px;
  align-content: center;
  margin-left: 8px;

  @media ${mediaQuery.mobile} {
    margin-left: 0;
  }
`;

const TitleWrapper = styled(motion.div)`
  gap: 4px;
  display: flex;
  flex-direction: column;
  flex: 1;
  align-content: center;
  justify-content: center;
  cursor: pointer;
`;

const Title = styled.small`

  @media ${mediaQuery.desktop} {
    font-size: smaller;
  }
`;
